import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useConferencesService } from '../../Context/ConferenceContext';

import NewConferenceDetails from './NewConferenceDetails';
import ConferenceParticipants from './ConferenceParticipants';
import PollManagementModal from './PollManagementModal';

import { useTranslate } from '@tolgee/react';
import { useGenericComponentsStateService } from '../../Context/GenericComponentsStateContext';
import { useNotify } from '../../Helpers/Notifications';
import { getApiErrorMessage } from '../../Helpers/apiErrorMessage';
import { CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER, CONFERENCE_ROLE_ATTENDEE, PRODUCT_SLUG_CONFERENCES } from '../../Constants/constantValues';
import { useAuthService } from '../../Context/AuthContext';
import useToolProject from '../../hooks/useToolProject';

const AddNewConferenceModal = ({
    contacts,
    loadConferences,
}) => {
    const { t } = useTranslate();
    const { notifyError, notifySuccess } = useNotify();
    const { setIsAddNewMeetingOpen, setLoading } = useGenericComponentsStateService();
    const { user } = useAuthService();
    const { projectId, projects, setSelectedId, isMultiple } = useToolProject(PRODUCT_SLUG_CONFERENCES);

    const {
        createConference,
        createPollQuestion,
        createPollOption,
    } = useConferencesService();

    const [organizers, setOrganizers] = useState([]);
    const [initialOrganizers, setInitialOrganizers] = useState([]);

    const [presenters, setPresenters] = useState([]);
    const [initialPresenters, setInitialPresenters] = useState([]);

    const [otherParticipants, setOtherParticipants] = useState([]);
    const [initialOtherParticipants, setInitialOtherParticipants] = useState([]);

    const [addParticipants, setAddParticipants] = useState(false);
    const [activeRole, setActiveRole] = useState(CONFERENCE_ROLE_PRESENTER);
    const allowMic = false;
    const allowCamera = false;
    // const allowQnA = false;
    const [allowChat, setAllowChat] = useState(false);
    const [allowPolls, setAllowPolls] = useState(false);
    const [allowInPersonJoining, setAllowInPersonJoining] = useState(false);
    const [allowAutoApprove, setAllowAutoApprove] = useState(false);
    const inviteAllOrgUsers = true;
    const publicRegistration = true;
    const initialValues = {
        conferenceTitle: "",
        conferenceDetails: "",
        conferenceTermsAndConditions: "",
    };

    const [inputValues, setInputValues] = useState(initialValues);

    const getNextHalfHour = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = minutes < 30 ? 30 : 60;
        now.setMinutes(roundedMinutes, 0, 0);
        return now;
    };

    const defaultStart = getNextHalfHour();
    const defaultEnd = new Date(defaultStart.getTime() + 60 * 60 * 1000);

    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    const [headerImageFile, setHeaderImageFile] = useState(null);

    const [localPolls, setLocalPolls] = useState([]);
    const [managePollsOpen, setManagePollsOpen] = useState(false);
    const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState(null);

    useEffect(() => {
        if (!headerImageFile) {
            setHeaderImagePreviewUrl(null);
            return undefined;
        }
        const previewUrl = URL.createObjectURL(headerImageFile);
        setHeaderImagePreviewUrl(previewUrl);
        return () => URL.revokeObjectURL(previewUrl);
    }, [headerImageFile]);

    const handleInputChange = (e) => {
        const { name, value } = e.currentTarget;
        setInputValues({
            ...inputValues,
            [name]: value,
        });
    };

    const placeCurrentUserFirst = (list) => {
        if (!user) return list;
        const filtered = list.filter((item) => item.id !== user.id);
        return [user, ...filtered];
    };

    const ensureCurrentUserOrganizer = () => {
        if (!user) return;
        setOrganizers((prev) => {
            if (prev.find((item) => item.id === user.id)) return placeCurrentUserFirst(prev);
            return [user, ...prev];
        });
        setInitialOrganizers((prev) => {
            if (prev.find((item) => item.id === user.id)) return placeCurrentUserFirst(prev);
            return [user, ...prev];
        });
    };

    const saveParticipantsForRole = () => {
        setInitialOrganizers(organizers);
        setInitialPresenters(presenters);
        setInitialOtherParticipants(otherParticipants);
        setAddParticipants(false);
    };

    const resetParticipantsForRole = () => {
        setOrganizers(initialOrganizers);
        setPresenters(initialPresenters);
        setOtherParticipants(initialOtherParticipants);
        setAddParticipants(false);
    };

    const normalizeOtherParticipantsEmails = (participants) =>
        (participants || [])
            .map((participant) => {
                if (typeof participant === 'string') return participant.trim();
                return participant?.email || participant?.name || '';
            })
            .filter(Boolean);

    const buildCreateConferencePayload = (dataObject) => {
        if (!headerImageFile) return dataObject;

        const formData = new FormData();
        formData.append('title', dataObject.title);
        formData.append('description', dataObject.description || '');
        formData.append('terms_and_conditions', dataObject.terms_and_conditions || '');
        formData.append('start_at', dataObject.start_at);
        formData.append('end_at', dataObject.end_at);

        (dataObject.organizer_ids || []).forEach((id) => formData.append('organizer_ids[]', id));
        (dataObject.participants_ids || []).forEach((id) => formData.append('participants_ids[]', id));

        normalizeOtherParticipantsEmails(dataObject.other_participants).forEach((email) => {
            formData.append('other_participants[]', email);
        });

        formData.append('allow_mic', String(dataObject.allow_mic));
        formData.append('allow_camera', String(dataObject.allow_camera));
        formData.append('allow_chat', String(dataObject.allow_chat));
        formData.append('allow_polls', String(dataObject.allow_polls));
        formData.append('allow_in_person_joining', String(dataObject.allow_in_person_joining));
        formData.append('auto_approve_participants', String(dataObject.auto_approve_participants));
        formData.append('invite_all_org_users', String(dataObject.invite_all_org_users));
        formData.append('public_registration', String(dataObject.public_registration));

        if (dataObject.project_id) {
            formData.append('project_id', dataObject.project_id);
        }

        formData.append('header_image', headerImageFile);
        return formData;
    };

    const createPollsForConference = (conferenceId) => {
        if (!localPolls || localPolls.length === 0) {
            setIsAddNewMeetingOpen(false);
            setLoading(false);
            loadConferences();
            notifySuccess(t('conferences.created_success'));
            return;
        }

        let pollsCreated = 0;
        const totalPolls = localPolls.length;
        let hasErrors = false;

        const checkComplete = () => {
            pollsCreated++;
            if (pollsCreated === totalPolls) {
                setIsAddNewMeetingOpen(false);
                setLoading(false);
                loadConferences();
                if (hasErrors) {
                    notifyError(t('conferences.polls_partial_failure'));
                } else {
                    notifySuccess(t('conferences.created_success'));
                }
            }
        };

        localPolls.forEach((poll, index) => {
            const validOptions = poll.options.filter(
                (opt) => opt.option_text && opt.option_text.trim().length > 0
            );

            if (!poll.question_text || validOptions.length < 2) {
                checkComplete();
                return;
            }

            createPollQuestion(
                conferenceId,
                poll.question_text,
                poll.answer_type || 'single',
                index,
                (response) => {
                    const questionId = response?.data?.poll?.id;
                    if (!questionId) {
                        hasErrors = true;
                        checkComplete();
                        return;
                    }

                    let optionsCompleted = 0;
                    validOptions.forEach((option, optIndex) => {
                        createPollOption(
                            questionId,
                            option.option_text,
                            option.position !== undefined ? option.position : optIndex,
                            () => {
                                optionsCompleted++;
                                if (optionsCompleted === validOptions.length) {
                                    checkComplete();
                                }
                            },
                            () => {
                                hasErrors = true;
                                notifyError(t('conferences.option_creation_failed'))
                                optionsCompleted++;
                                if (optionsCompleted === validOptions.length) {
                                    checkComplete();
                                }
                            }
                        );
                    });
                },
                () => {
                    hasErrors = true;
                    notifyError(t('conferences.question_creation_failed'));
                    checkComplete();
                }
            );
        });
    };

    const createNewConference = () => {
        setLoading(true);
        const organizerIds = organizers.map((contact) => contact.id);
        const presenterIds = presenters.map((contact) => contact.id);

        const dataObject = {
            title: inputValues.conferenceTitle,
            description: inputValues.conferenceDetails,
            terms_and_conditions: inputValues.conferenceTermsAndConditions,
            start_at: startDate.toISOString(),
            end_at: endDate.toISOString(),
            participants_ids: presenterIds,
            organizer_ids: organizerIds,
            allow_mic: allowMic,
            allow_camera: allowCamera,
            allow_chat: allowChat,
            allow_polls: allowPolls,
            allow_in_person_joining: allowInPersonJoining,
            auto_approve_participants: allowAutoApprove,
            invite_all_org_users: inviteAllOrgUsers,
            public_registration: publicRegistration,
        };

        if (otherParticipants.length > 0) {
            dataObject.other_participants = otherParticipants;
        }

        if (projectId) {
            dataObject.project_id = projectId;
        }

        const payload = buildCreateConferencePayload(dataObject);

        createConference(
            payload,
            (response) => {
                const conferenceId = response?.data?.conference?.id;
                if (conferenceId && allowPolls) {
                    createPollsForConference(conferenceId);
                } else {
                    setIsAddNewMeetingOpen(false);
                    setLoading(false);
                    loadConferences();
                    notifySuccess(t('conferences.created_success'));
                }
            },
            (error) => {
                setLoading(false);
                notifyError(getApiErrorMessage(error, t, {
                    statusMap: {
                        403: 'conferences.subscription_required_error',
                    },
                    validationMessageMap: {
                        "Title can't be blank": 'general.error_title_required',
                        'must be after the start time': 'general.error_end_before_start',
                        'do not exist': 'general.error_invalid_participants',
                        'must be a PNG, JPG, JPEG, or WEBP image': 'conferences.error_invalid_header_image_type',
                        'must be smaller than 10MB': 'conferences.error_header_image_too_large',
                    },
                }));
            }
        );
    };

    const handleCreateConference = () => {
        createNewConference();
    };

    const handleCloseAddNewConference = () => {
        setIsAddNewMeetingOpen(false);
        const baseOrganizer = user ? [user] : [];
        setOrganizers(baseOrganizer);
        setInitialOrganizers(baseOrganizer);
        setPresenters([]);
        setInitialPresenters([]);
        setOtherParticipants([]);
        setInitialOtherParticipants([]);
        setAddParticipants(false);
        setInputValues(initialValues);
        setStartDate(defaultStart);
        setEndDate(defaultEnd);
        setHeaderImageFile(null);
        setLocalPolls([]);
        setAllowAutoApprove(false);
        setAllowInPersonJoining(false);
    };

    useEffect(() => {
        ensureCurrentUserOrganizer();
    }, [user]);

    useEffect(() => {
        const startDateCheck = new Date(startDate);
        startDateCheck.setHours(0, 0, 0, 0);

        const endDateCheck = new Date(endDate);
        endDateCheck.setHours(0, 0, 0, 0);

        const startTime = startDate.getTime();
        const endTime = endDate.getTime();

        if (endDateCheck < startDateCheck) {
            setEndDate(new Date(startDate));
        }

        if (endDateCheck.getTime() === startDateCheck.getTime() && endTime < startTime) {
            const updatedEndDate = new Date(startDate);
            updatedEndDate.setMinutes(startDate.getMinutes() + 15);
            setEndDate(updatedEndDate);
        }

    }, [startDate, endDate]);

    return (
        <Container>
            {addParticipants &&
                <ConferenceParticipants
                    selectedParticipants={
                        activeRole === CONFERENCE_ROLE_ORGANIZER ? organizers : presenters
                    }
                    initialParticipants={
                        activeRole === CONFERENCE_ROLE_ORGANIZER ? initialOrganizers : initialPresenters
                    }
                    setSelectedParticipants={
                        activeRole === CONFERENCE_ROLE_ORGANIZER ? setOrganizers : setPresenters
                    }
                    handleSaveBtn={saveParticipantsForRole}
                    handleBackBtn={resetParticipantsForRole}
                    otherParticipants={activeRole === CONFERENCE_ROLE_ATTENDEE ? otherParticipants : []}
                    setOtherParticipants={activeRole === CONFERENCE_ROLE_ATTENDEE ? setOtherParticipants : () => {}}
                    initialOtherParticipants={activeRole === CONFERENCE_ROLE_ATTENDEE ? initialOtherParticipants : []}
                    activeRole={activeRole}
                    contacts={contacts}
                />}

            {managePollsOpen && (
                <PollManagementModal
                    conferenceId={null}
                    localPolls={localPolls}
                    setLocalPolls={setLocalPolls}
                    onClose={() => setManagePollsOpen(false)}
                />
            )}

            <MenuContainer>
                <NewConferenceDetails
                    handleInputChange={handleInputChange}
                    inputValues={inputValues}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    organizers={organizers}
                    presenters={presenters}
                    setAddParticipants={setAddParticipants}
                    setActiveRole={setActiveRole}
                    handleCreateConference={handleCreateConference}
                    otherParticipants={otherParticipants}
                    handleCloseAddNewConference={handleCloseAddNewConference}
                    allowMic={allowMic}
                    allowCamera={allowCamera}
                    allowChat={allowChat}
                    setAllowChat={setAllowChat}
                    allowPolls={allowPolls}
                    setAllowPolls={setAllowPolls}
                    allowInPersonJoining={allowInPersonJoining}
                    setAllowInPersonJoining={setAllowInPersonJoining}
                    allowAutoApprove={allowAutoApprove}
                    setAllowAutoApprove={setAllowAutoApprove}
                    headerImageFile={headerImageFile}
                    headerImagePreviewUrl={headerImagePreviewUrl}
                    setHeaderImageFile={setHeaderImageFile}
                    localPolls={localPolls}
                    setManagePollsOpen={setManagePollsOpen}
                    projects={projects}
                    projectId={projectId}
                    setProjectId={setSelectedId}
                    isMultipleProjects={isMultiple}
                />
            </MenuContainer>

        </Container>
    );
};

export default AddNewConferenceModal;

const Container = styled.div`
 background: ${({ theme }) => theme.chat.conversationBackground};
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  margin:auto;
  overflow: auto;
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.menu.background};
height:100%;
overflow:hidden;
width:100%;
  gap: 16px;
`;
